import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { CeiBaseApiService } from '@cei-ufg/ngx-arquitetura-cei-commons';
import {
  CeiBaseListApiService,
  CeiTablePage,
  CeiTablePageRequest,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { PessoasApiService } from '../../api/generated';
import { PessoaModel } from './pessoa.model';
import { normalizePessoaFotoMeUrl, PESSOA_FOTO_ME_API_PATH } from './pessoa-foto.paths';
import { higienizarPayloadPessoa } from './pessoa-payload.util';

/**
 * CRUD de pessoa via HTTP autenticado (Bearer) no `apiUrl` configurado.
 * Resolução sempre por **login** — vale igual para domain local ou remoto.
 * Sem misturar `pessoa_id`/PK de um ambiente com operações em outro.
 *
 * Upload/remoção de foto usa o cliente OpenAPI gerado (`PessoasApiService`).
 */
@Injectable({ providedIn: 'root' })
export class PessoaApiService extends CeiBaseApiService implements CeiBaseListApiService<PessoaModel> {
  /** @see PESSOA_FOTO_ME_API_PATH */
  static readonly FOTO_ME_PATH = PESSOA_FOTO_ME_API_PATH;

  private readonly pessoasApi = inject(PessoasApiService);

  list(request: CeiTablePageRequest): Observable<CeiTablePage<PessoaModel>> {
    const params = new HttpParams()
      .set('page', String(request.page))
      .set('page_size', String(request.pageSize));

    return this.get<CeiTablePage<PessoaModel>>('/api/pessoa/', params);
  }

  create(model: PessoaModel): Observable<PessoaModel> {
    return this.createMe(model);
  }

  retrieve(id: number | string): Observable<PessoaModel> {
    return this.get<PessoaModel>(`/api/pessoa/${id}`).pipe(map((payload) => this.mapApiToModel(payload)));
  }

  /** GET da pessoa do login autenticado no domínio configurado. */
  retrieveMe(): Observable<PessoaModel> {
    return this.get<PessoaModel>('/api/pessoa/usuario/autenticado').pipe(
      map((payload) => this.mapMeApiToModel(payload)),
    );
  }

  /**
   * POST do botão Cadastrar; associa ao Usuario do mesmo login no domínio configurado.
   */
  createMe(model: PessoaModel): Observable<PessoaModel> {
    return this.post<PessoaModel>(
      '/api/pessoa/usuario/autenticado',
      higienizarPayloadPessoa(model),
    ).pipe(map((payload) => this.mapMeApiToModel(payload)));
  }

  /** Atualiza a própria pessoa no domínio (PUT autenticado). */
  updateMe(model: PessoaModel): Observable<PessoaModel> {
    return this.put<PessoaModel>(
      '/api/pessoa/usuario/autenticado',
      higienizarPayloadPessoa(model),
    ).pipe(map((payload) => this.mapMeApiToModel(payload)));
  }

  update(_id: number | string, model: PessoaModel): Observable<PessoaModel> {
    return this.updateMe(model);
  }

  destroy(id: number | string): Observable<unknown> {
    return this.delete(`/api/pessoa/${id}`);
  }

  uploadFoto(_id: number | string, file: File): Observable<PessoaModel> {
    return this.uploadFotoMe(file);
  }

  uploadFotoMe(file: File): Observable<PessoaModel> {
    return this.pessoasApi
      .pessoaUsuarioAutenticadoFotoCreate({ foto: file })
      .pipe(map((payload) => this.mapMeApiToModel(payload as unknown as PessoaModel)));
  }

  deleteFoto(_id: number | string): Observable<unknown> {
    return this.deleteFotoMe();
  }

  deleteFotoMe(): Observable<unknown> {
    return this.pessoasApi.pessoaUsuarioAutenticadoFotoDestroy();
  }

  private mapMeApiToModel(payload: PessoaModel): PessoaModel {
    return {
      ...this.mapApiToModel(payload),
      foto_url: normalizePessoaFotoMeUrl(payload.foto_url),
    };
  }

  private mapApiToModel(payload: PessoaModel): PessoaModel {
    return {
      ...payload,
      documentos: payload.documentos ?? [],
      telefones: payload.telefones ?? [],
      enderecos: payload.enderecos ?? [],
    };
  }
}
